// src/pages/UserAdmin/Contacts.js — Página /contatos (CRM)
//
// Layout de 2 colunas dentro do padrão do sistema (AppSidebar + TopBar):
//   ┌───────────────────────────┬─────────────────────────┐
//   │ Lista de contatos         │ Detalhe do contato      │
//   │ (busca, filtros, paginado)│ (dados + notas)         │
//   └───────────────────────────┴─────────────────────────┘
//
// Endpoints consumidos (backend em app/routers/contacts.py):
//   GET    /contacts               → lista com filtros
//   POST   /contacts               → criar contato manual
//   GET    /contacts/{id}          → detalhe (também dá pra usar do list)
//   PATCH  /contacts/{id}          → editar interesse/reunião/labels/agente
//   DELETE /contacts/{id}          → excluir (só SUPERVISOR)
//   GET    /contacts/{id}/notes    → listar anotações
//   POST   /contacts/{id}/notes    → criar
//   DELETE /contacts/{id}/notes/{note_id} → excluir (autor ou supervisor)
//   GET    /contacts/export.csv    → export
//
// Escopo: AGENT e SUPERVISOR veem os mesmos contatos da company.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, TextField, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiPlus, FiTrash2, FiEdit2, FiAlertTriangle, FiFolder, FiFolderPlus,
  FiMoreHorizontal, FiCheck, FiUser, FiUserX,
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import TopBar from '../../components/TopBar';
import api from '../../services/Api';
// CRM redesign (set/26): design system isolado em crm/*. Aqui só
// importamos os componentes usados nesta página — outros artefatos
// (drawer, bulk bar, tabela, topo, menus) foram extraídos pra manter esse
// arquivo como orquestrador em vez de página gigante.
//
// Out/26 (CRM moderno, Claude Design "CRM Moderno.dc.html"): o topo
// (crm/Topo), a lista (crm/TabelaContatos) e os menus (crm/MenuCrm) saíram
// daqui; as cores viraram os tokens `--ch-*`/`--cx-*` (crm/crm.css), e a
// tela ganhou o tema escuro do chat, da Agenda e dos Formulários.
import ContactDrawer from './crm/ContactDrawer';
import {
  CRM_THEME, INTEREST_LABELS, INTEREST_ORDER, OUTFIT, SYNE, UNSET, folderColor, labelColor, temaMui,
} from './crm/theme';
import BulkActionBar from './crm/BulkActionBar';
import BulkTemplateDialog from './crm/BulkTemplateDialog';
import EscopoSelecao from './crm/EscopoSelecao';
import ImportCsvDialog from './crm/ImportCsvDialog';
import ContatoExisteDialog from './crm/ContatoExisteDialog';
import LembretesDialog from './crm/LembretesDialog';
import CustomFieldsDialog from './crm/CustomFieldsDialog';
import { DivisorMenu, ItemMenu, MenuCrm, NotaMenu } from './crm/MenuCrm';
import { Aviso, BarraFerramentas, BarraTemperatura, FiltrosInteresse, FiltrosLinha, ROTULO_FILTRO_QUALIF } from './crm/Topo';
import PainelQualificar from '../../components/qualificacao/PainelQualificar';
import { DESFAZER_MS, MOTIVOS, STATUS as QUALIF } from '../../components/qualificacao/regras';
import TabelaContatos, { colunas } from './crm/TabelaContatos';
import './crm/crm.css';

// Mesma chave do chat, da Agenda e dos Formulários: o tema escolhido numa
// tela vale nas outras.
const CHAVE_TEMA = 'cetto:chat-theme';

// ─── Constantes ────────────────────────────────────────────────────────
// `UNSET` (o "Não classificado") e os rótulos de interesse moram em
// crm/theme.js. No banco é NULL — ver models.INTEREST_UNSET no backend.
// Sai na URL/body como string porque `null` num query param vira a
// ausência do param.

const PER_PAGE = 20;

// As colunas da tabela vivem em crm/TabelaContatos (`colunas`): cabeçalho,
// faixa de alcance e linhas PRECISAM usar a mesma definição. Até set/26
// elas repetiam o valor inline, e o SilenceBadge ("X dias sem resposta")
// era filho DIRETO do grid, condicional: nos contatos silenciosos virava
// uma célula extra e empurrava agente e interesse pra coluna seguinte.

// As seis cores de pasta, na ordem do seletor. As cores em si são as do
// tema (`folderColor`), que trocam com o escuro.
const FOLDER_COLOR_KEYS = ['blue', 'green', 'amber', 'purple', 'pink', 'red'];
const NOME_COR = { blue: 'Azul', green: 'Verde', amber: 'Âmbar', purple: 'Roxo', pink: 'Rosa', red: 'Vermelho' };

// ─── Página ────────────────────────────────────────────────────────────
export default function Contacts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const theme = useTheme();
  // Layout master/detail em telas < md: só a lista até um contato ser
  // selecionado; depois, só o detalhe (com botão voltar na TopBar).
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // ADMIN entra junto. Out/26: isto era `=== 'SUPERVISOR'` e escondia do
  // ADMIN — o dono da conta! — o botão "Importar", a coluna de
  // responsável, os campos personalizados e o resto do que a tela trata
  // como "privilegiado". O backend sempre aceitou os dois
  // (`_is_supervisor_or_admin` em contacts.py), então não era permissão:
  // era a tela escondendo o que a API liberava, e o dono concluindo que a
  // importação de contatos não existia.
  //
  // O nome fica `isSupervisor` de propósito — são 13 usos no arquivo, e
  // renomear misturaria o conserto com ruído de diff.
  const isSupervisor = ['SUPERVISOR', 'ADMIN'].includes(user?.role);

  // Tema claro/escuro (out/26): `data-theme="dark"` no <html> troca os
  // tokens `--ch-*`/`--cx-*`; o tema do MUI acompanha pra os diálogos,
  // menus e campos não abrirem brancos no escuro.
  const [tema, setTema] = useState(() => {
    try { return localStorage.getItem(CHAVE_TEMA) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  useLayoutEffect(() => {
    const el = document.documentElement;
    if (tema === 'dark') el.setAttribute('data-theme', 'dark'); else el.removeAttribute('data-theme');
    return () => el.removeAttribute('data-theme');
  }, [tema]);
  const trocarTema = useCallback(() => setTema((t) => {
    const n = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(CHAVE_TEMA, n); } catch { /* storage indisponível */ }
    return n;
  }), []);
  const muiTema = useMemo(() => temaMui(tema), [tema]);

  // Lista
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [counts, setCounts] = useState({ [UNSET]: 0, ALTO: 0, MEDIO: 0, BAIXO: 0, FRIO: 0 });
  // Quantos contatos estão sem agente responsável — vem separado do
  // breakdown por interesse porque é outro eixo (quem cuida × temperatura).
  const [unassigned, setUnassigned] = useState(0);
  // Filtro por responsável: null = todos, 'none' = sem responsável. O
  // backend também aceita um user_id, mas a UI hoje só expõe o 'none' —
  // filtrar por agente específico já é o trabalho do próprio agente.
  const [assignedFilter, setAssignedFilter] = useState(null);
  // "Veio de anúncio" (out/26, rastreamento PR 1): null = todos, 'anuncio' =
  // só quem chegou por um anúncio "Clique para o WhatsApp". `deAnuncio` é o
  // tamanho desse grupo, no mesmo escopo da lista.
  const [origemFilter, setOrigemFilter] = useState(null);
  const [deAnuncio, setDeAnuncio] = useState(0);
  // Qualificação do lead (out/26, rastreamento PR 2): o filtro
  // ('qualificado' | 'desqualificado' | 'venda' | 'sem') e a contagem de
  // cada um no mesmo escopo da lista; o menu do filtro e o painel do lote.
  const [qualifFilter, setQualifFilter] = useState(null);
  const [contagemLead, setContagemLead] = useState(null);
  const [qualifMenuAnchor, setQualifMenuAnchor] = useState(null);
  const [bulkQualifAnchor, setBulkQualifAnchor] = useState(null);
  const avisoTimerRef = useRef(null);
  // UM timer pro aviso da tela: cada aviso novo cancela o do anterior
  // (code-review de out/26 — o timer de um aviso velho apagava o "Desfazer"
  // do lote antes dos 10 s).
  const fecharAvisoEm = useCallback((ms) => {
    clearTimeout(avisoTimerRef.current);
    avisoTimerRef.current = setTimeout(() => setBulkResult(null), ms);
  }, []);
  // O desfazer do lote em andamento (clique duplo mandava dois).
  const desfazendoRef = useRef(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [sort, setSort] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [listError, setListError] = useState('');

  // Detalhe
  // ─── Qual contato está aberto: a URL é a fonte ÚNICA ──────────────────
  // Out/26. Antes isto era `useState` e a URL (`?selected=`) era um
  // segundo dono da mesma informação, sincronizado só num sentido
  // (URL → state). Daí saiu o bug do "X que não fecha":
  //
  //   1. o ChatPage navega pra /contatos?selected=A
  //   2. o efeito de deep-link lê o parâmetro e faz setSelectedId('A')
  //   3. a pessoa clica no X → setSelectedId(null)
  //   4. o efeito tem `selectedId` nas dependências, então roda de novo —
  //      o `?selected=A` AINDA está na URL, e ele reabre o contato
  //
  // O X disparava; o efeito desfazia no frame seguinte. Mesmo caminho pro
  // ESC, pro clique fora e pro voltar do mobile.
  //
  // Derivar da URL mata o laço (fechar APAGA o parâmetro, então não há o
  // que reabrir) e de quebra conserta três coisas que vinham juntas: F5
  // não reabre contato sozinho, o botão Voltar do navegador fica
  // coerente, e clicar em outra linha da lista não é mais sequestrado de
  // volta pro contato do deep-link.
  //
  // `replace: true` pra abrir contato não empilhar histórico: quem veio
  // do chat e fecha o drawer volta pro chat no Voltar, e não pra dez
  // estados intermediários da mesma tela.
  const selectedId = searchParams.get('selected');
  const setSelectedId = useCallback((id) => {
    const p = new URLSearchParams(searchParams);
    if (id) p.set('selected', id);
    else p.delete('selected');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const selected = useMemo(() => items.find(c => c.id === selectedId) || null, [items, selectedId]);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  // `noteText` global saiu no redesign — o composer do ContactDrawer
  // usa state local. Handler agora é `addNoteFromDrawer(text)`.
  const [noteSaving, setNoteSaving] = useState(false);

  // Dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newSaving, setNewSaving] = useState(false);
  const [newError, setNewError] = useState('');
  // Conflito de número já cadastrado: o 409 estruturado que o backend
  // devolve, com o contato existente dentro. Ver ContatoExisteDialog.
  const [conflito, setConflito] = useState(null);
  const [conflitoSalvando, setConflitoSalvando] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Menu status inline (interesse/reunião) — usado pela versão antiga da
  // página. No CRM redesign a edição de interesse/reunião foi absorvida
  // pelo ContactDrawer (Select inline). State ainda declarado com prefixo
  // `_` pra suprimir o warning até o próximo cleanup.
  const [_statusMenu, _setStatusMenu] = useState({ anchor: null, kind: null }); // eslint-disable-line no-unused-vars

  // ─── Pastas (Folder) ────────────────────────────────────────────────────
  // Pastas convivem com interest_status (Alto/Médio/Baixo/Frio) e Labels.
  // Cardinalidade many-to-many: um contato pode estar em N pastas.
  const [folders, setFolders] = useState([]);
  const [folderFilter, setFolderFilter] = useState(null);  // folder_id ou null
  // `foldersLoading` state segue existindo pro setter do fetchFolders;
  // valor não é lido em lugar nenhum ainda. Prefixo `_` só pra silenciar
  // o warning até a próxima faxina.
  const [_foldersLoading, setFoldersLoading] = useState(false); // eslint-disable-line no-unused-vars

  // Modal criar pasta
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('blue');
  const [folderSaving, setFolderSaving] = useState(false);
  const [folderError, setFolderError] = useState('');

  // Menu por pasta (renomear / apagar) — anchor + folder alvo
  const [folderMenu, setFolderMenu] = useState({ anchor: null, folder: null });

  // ─── Etiquetas (Label) ─────────────────────────────────────────────────
  // Compartilhadas com o chat (mesmo /labels endpoint). No CRM, servem só
  // pra FILTRAR — gestão (criar/renomear/apagar) continua no chat.
  // Multi-select com semântica OR: mostra contatos que tenham qualquer uma
  // das etiquetas selecionadas.
  const [labels, setLabels] = useState([]);
  const [activeLabelIds, setActiveLabelIds] = useState(new Set());

  // Anchors dos dropdowns de filtro (Pastas / Etiquetas). Padrão UX de
  // CRMs modernos (Linear/Airtable/Notion): chip compacto com ▾ que revela
  // menu ancorado. Evita poluição visual quando há muitas pastas/etiquetas.
  // Menus de edição inline da tabela. Guardam { anchor, contact } porque
  // o menu precisa saber em QUAL linha foi aberto — diferente dos menus de
  // lote, que agem sobre `selectedIds`.
  const [rowAgentMenu, setRowAgentMenu] = useState(null);
  const [rowInterestMenu, setRowInterestMenu] = useState(null);
  const [foldersMenuAnchor, setFoldersMenuAnchor] = useState(null);
  const [labelsMenuAnchor, setLabelsMenuAnchor] = useState(null);

  // Modal de editar contato — { name, phone_number } quando aberto, null quando fechado.
  const [editOpen, setEditOpen] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Multi-select de contatos + toolbar bulk
  const [selectedIds, setSelectedIds] = useState(new Set());
  // "Toda a pasta" da faixa de alcance (crm/EscopoSelecao, out/26): os ids
  // de TODOS os contatos do filtro, pedidos ao servidor. `n` é quantos
  // entraram; a seleção está nesse modo enquanto `selectedIds` tiver
  // exatamente esses `n` — desmarcar uma linha já tira do modo, sem flag
  // separada pra ficar dessincronizada.
  // { status: 'carregando' | 'ok' | 'erro', n, total }
  const [selecaoTudo, setSelecaoTudo] = useState(null);
  // Resposta que chega depois de a pessoa mudar de filtro (ou desistir)
  // não pode selecionar os ids do filtro antigo.
  const pedidoTudoRef = useRef(0);
  const limparSelecao = useCallback(() => {
    pedidoTudoRef.current += 1;
    setSelecaoTudo(null);
    setSelectedIds(new Set());
  }, []);
  // Diálogo de definição dos campos personalizados (só supervisor abre).
  const [customFieldsOpen, setCustomFieldsOpen] = useState(false);
  const [bulkFolderAnchor, setBulkFolderAnchor] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);   // {added, alreadyIn, folderName}

  // ─── Bulk avançado (endpoint /contacts/bulk) ───────────────────────────
  // Ações que não são "adicionar a pasta" — atribuir agente, mudar
  // interesse, etiquetas, delete. Anchor no lugar de dialogs cheios pra
  // manter a barra flutuante consistente com o padrão de menu-do-hover.
  const [bulkAgentAnchor, setBulkAgentAnchor] = useState(null);
  const [bulkLabelsAnchor, setBulkLabelsAnchor] = useState(null);
  const [bulkInterestAnchor, setBulkInterestAnchor] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  // Disparo de template em lote. Guarda os ids num estado próprio em vez de
  // ler `selectedIds` direto: o diálogo faz a conferência e o envio em
  // cima de uma lista que não pode mudar no meio do caminho.
  const [bulkTemplateIds, setBulkTemplateIds] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [lembretesOpen, setLembretesOpen] = useState(false);

  // `?lembretes=1` abre a lista de lembretes direto (out/26): é o destino do
  // "Ver agendamento →" do card do Copiloto. O parâmetro sai da URL logo
  // depois, senão um F5 reabriria o diálogo sem ninguém pedir.
  useEffect(() => {
    if (searchParams.get('lembretes') !== '1') return;
    setLembretesOpen(true);
    const p = new URLSearchParams(searchParams);
    p.delete('lembretes');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // ─── Abrir conversa a partir do contato ────────────────────────────────
  // Lead criado pelo CRM nasce com `assigned_device_id` NULL — ninguém
  // escolheu chip pra ele ainda. Até out/26 o handler era
  // `c.assigned_device_id && navigate(...)`, então pra esses contatos o
  // botão "Abrir conversa" do drawer simplesmente NÃO FAZIA NADA: sem
  // erro, sem aviso, sem navegação. Quem clicava achava que o sistema
  // tinha travado.
  //
  // A navegação em si sempre funcionou — o ChatPage busca o contato por id
  // e o insere na lista quando ele ainda não teve conversa. O que faltava
  // era resolver um chip.
  const [devices, setDevices] = useState([]);
  const [escolherChip, setEscolherChip] = useState(null);  // contato aguardando chip

  useEffect(() => {
    if (!token) return;
    api.get('/devices/', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDevices(Array.isArray(res.data) ? res.data : (res.data?.devices || [])))
      .catch(() => setDevices([]));
  }, [token]);

  const abrirConversa = useCallback((contato) => {
    if (!contato) return;
    if (contato.assigned_device_id) {
      navigate(`/chat/${contato.assigned_device_id}/${contato.id}`);
      return;
    }
    const ativos = devices.filter(d => !d.deleted_at);
    if (ativos.length === 1) {
      // Um chip só: perguntar qual seria uma pergunta de uma alternativa.
      navigate(`/chat/${ativos[0].id}/${contato.id}`);
      return;
    }
    if (ativos.length > 1) {
      setEscolherChip(contato);
      return;
    }
    // Zero chips: o clique continua não navegando, mas agora diz por quê.
    setBulkResult({
      message: 'Nenhum chip conectado. Conecte um número em Meta → Chips '
        + 'para poder conversar.',
      severity: 'warning',
    });
    fecharAvisoEm(6000);
  }, [devices, navigate, fecharAvisoEm]);

  const fetchFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const res = await api.get('/folders', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFolders(res.data || []);
    } catch {
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  // Fetch etiquetas da company — uma vez no mount. Mesmo endpoint que o
  // chat usa (labels são compartilhadas). Se o cliente cria/apaga labels
  // pelo chat, refletimos aqui só ao próximo mount — trade-off aceitável.
  const fetchLabels = useCallback(async () => {
    try {
      const res = await api.get('/labels', { headers: { Authorization: `Bearer ${token}` } });
      setLabels(res.data || []);
    } catch {
      setLabels([]);
    }
  }, [token]);
  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  // ─── Agentes da empresa ────────────────────────────────────────────────
  // `GET /users/agents` já filtra por company e é supervisor-only — agente
  // comum recebe 403, por isso só chamamos quando `isSupervisor`.
  //
  // Antes o menu de atribuição em lote montava a lista a partir dos
  // contatos JÁ atribuídos que estavam na página (`items.filter(c =>
  // c.assigned_user_id)`). Agente recém-criado, ou que ainda não tinha
  // contato nesta página, simplesmente não aparecia — não dava pra
  // atribuir o primeiro contato pra ele.
  const [agents, setAgents] = useState([]);
  const fetchAgents = useCallback(async () => {
    if (!isSupervisor) { setAgents([]); return; }
    try {
      const res = await api.get('/users/agents', { headers: { Authorization: `Bearer ${token}` } });
      setAgents((res.data || []).filter(a => a.is_active !== false));
    } catch {
      setAgents([]);
    }
  }, [token, isSupervisor]);
  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { setFolderError('Dá um nome pra ela.'); return; }
    setFolderSaving(true);
    setFolderError('');
    try {
      const res = await api.post('/folders', { name, color: newFolderColor }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFolders(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewFolderOpen(false);
      setNewFolderName('');
      setNewFolderColor('blue');
    } catch (err) {
      setFolderError(err.response?.data?.detail || 'Erro ao criar pasta.');
    } finally {
      setFolderSaving(false);
    }
  };

  const deleteFolder = async (folder) => {
    const ok = window.confirm(
      `Excluir a pasta "${folder.name}"?\n\nOs contatos NÃO são apagados — só saem da pasta.`
    );
    if (!ok) return;
    // Optimistic
    setFolders(prev => prev.filter(f => f.id !== folder.id));
    if (folderFilter === folder.id) setFolderFilter(null);
    setFolderMenu({ anchor: null, folder: null });
    try {
      await api.delete(`/folders/${folder.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Refetch da lista pra remover a pasta das rows.
      fetchList();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir pasta.');
      fetchFolders();
    }
  };

  const renameFolder = async (folder) => {
    const novoNome = window.prompt(`Novo nome para "${folder.name}":`, folder.name);
    if (!novoNome || novoNome.trim() === folder.name) {
      setFolderMenu({ anchor: null, folder: null });
      return;
    }
    setFolderMenu({ anchor: null, folder: null });
    try {
      const res = await api.patch(`/folders/${folder.id}`, { name: novoNome.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFolders(prev => prev.map(f => f.id === folder.id ? res.data : f)
        .sort((a, b) => a.name.localeCompare(b.name)));
      fetchList();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao renomear.');
    }
  };

  const bulkAddToFolder = async (folder) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkFolderAnchor(null);
    try {
      const res = await api.post(
        `/folders/${folder.id}/clients`,
        { client_ids: ids },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBulkResult({ ...res.data, folderName: folder.name });
      limparSelecao();
      fetchList();
      fecharAvisoEm(3500);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao mover contatos.');
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // O checkbox do cabeçalho marca ESTA PÁGINA; "toda a pasta" é a faixa
  // logo abaixo dele. Até out/26 a conta era `selectedIds.size ===
  // items.length`, que só funcionava com a seleção presa a uma página —
  // com a pasta inteira marcada, o cabeçalho de qualquer página aparecia
  // como "parcial" e o clique trocava os 127 pelos 20 da tela.
  const paginaToda = items.length > 0 && items.every(c => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    if (paginaToda) {
      limparSelecao();
    } else {
      pedidoTudoRef.current += 1;
      setSelecaoTudo(null);
      setSelectedIds(new Set(items.map(c => c.id)));
    }
  };

  // ─── Bulk actions via /contacts/bulk (endpoint unificado) ──────────────
  const doBulkAction = useCallback(async (action, extra = {}) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkSaving(true);
    try {
      const res = await api.post(
        '/contacts/bulk',
        { action, client_ids: ids, ...extra },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBulkResult({
        added: res.data.affected,
        alreadyIn: 0,
        folderName:
          action === 'delete' ? 'excluídos'
          // Usa o label da UI ("Médio", "Não classificado"), não a string
          // do protocolo — o feedback é lido por gente.
          : action === 'set_interest'
            ? (extra.interest_status === UNSET
                ? 'sem classificação'
                : `interesse ${(INTEREST_LABELS[extra.interest_status] || '').toLowerCase()}`)
          : action === 'assign_agent' ? 'atribuídos'
          : action === 'attach_labels' ? 'etiquetados'
          : 'atualizados',
      });
      limparSelecao();
      fetchList();
      fecharAvisoEm(3500);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao aplicar ação em lote.');
    } finally {
      setBulkSaving(false);
      setBulkAgentAnchor(null);
      setBulkLabelsAnchor(null);
      setBulkInterestAnchor(null);
      setBulkDeleteOpen(false);
    }
  }, [selectedIds, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Qualificação do lead (out/26, rastreamento PR 2) ─────────────────
  // Marcado na janela do contato: a linha e a contagem do filtro mudam na
  // hora, sem recarregar a lista (com o filtro "Sem marcação" ligado, o
  // recarregar tiraria da lista o contato que está aberto).
  const aoQualificar = (id, nova) => {
    const c = items.find(x => x.id === id);
    const antes = c?.qualificacao?.status || 'sem';
    const depois = nova?.status || 'sem';
    if (c && antes !== depois) {
      setContagemLead(cl => (cl ? { ...cl, [antes]: Math.max(0, (cl[antes] || 0) - 1), [depois]: (cl[depois] || 0) + 1 } : cl));
    }
    setItems(prev => prev.map(x => (x.id === id ? { ...x, qualificacao: nova } : x)));
  };

  const mostrarAviso = (aviso, ms) => {
    setBulkResult(aviso);
    fecharAvisoEm(ms);
  };
  useEffect(() => () => clearTimeout(avisoTimerRef.current), []);

  const desfazerLote = async (lote) => {
    if (desfazendoRef.current) return;
    desfazendoRef.current = true;
    clearTimeout(avisoTimerRef.current);
    // Some o botão na hora: um segundo clique não manda outro pedido.
    setBulkResult(r => (r ? { ...r, desfazerLote: null, message: 'Desfazendo…' } : r));
    try {
      const { data } = await api.post('/contacts/qualificacao/desfazer', { lote });
      const n = data.desfeitos;
      mostrarAviso({
        message: `Desfeito: ${n} contato${n !== 1 ? 's voltaram' : ' voltou'} ao que era${n !== 1 ? 'm' : ''}.`
          + (data.mantidos ? ` ${data.mantidos} marcado${data.mantidos !== 1 ? 's' : ''} de novo depois ficou como está.` : ''),
      }, 3500);
      fetchList();
    } catch (err) {
      mostrarAviso({ severity: 'error', message: err.response?.data?.detail || 'Não foi possível desfazer.' }, 6000);
    } finally {
      desfazendoRef.current = false;
    }
  };

  // Marcar em lote (barra de seleção). Lança erro com a frase do servidor:
  // o painel mostra e a pessoa corrige sem perder o que escolheu.
  const qualificarLote = async (pedido) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    let data;
    try {
      ({ data } = await api.post('/contacts/bulk', {
        action: 'set_lead_status', client_ids: ids,
        lead_status: pedido.status, motivo: pedido.motivo || null, motivo_texto: pedido.motivo_texto || null,
      }));
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Não foi possível marcar os contatos.');
    }
    const n = data.affected;
    const ja = data.ja_estavam || 0;
    const motivo = pedido.status === 'desqualificado'
      ? ` (${pedido.motivo === 'outro' ? pedido.motivo_texto : (MOTIVOS.find(([k]) => k === pedido.motivo) || [])[1]})` : '';
    const como = pedido.status === 'qualificado' ? 'qualificado' : 'desqualificado';
    let message;
    if (!n) message = 'Todos os contatos selecionados já estavam assim.';
    else if (!pedido.status) message = `Marcação removida de ${n} contato${n !== 1 ? 's' : ''}.`;
    else message = `${n} contato${n !== 1 ? 's' : ''} marcado${n !== 1 ? 's' : ''} como ${como}${n !== 1 ? 's' : ''}${motivo}.`;
    if (n && ja) message += ` ${ja} já estava${ja !== 1 ? 'm' : ''} assim.`;
    mostrarAviso({ message, desfazerLote: n ? data.lote : null }, n ? DESFAZER_MS : 3500);
    limparSelecao();
    fetchList();
  };

  // ─── Visões salvas: saíram da tela em out/26 ───────────────────────────
  // A barra de visões (chips + "+ Nova visão") foi removida daqui. Não era
  // redundante com Pastas — uma visão guardava a COMBINAÇÃO de busca,
  // interesse, responsável, pasta e etiquetas —, mas em produção nenhuma
  // company tinha criado uma sequer, e o botão ocupava a faixa de filtros
  // sem nunca ser usado.
  //
  // O backend continua inteiro de propósito: modelo `SavedFilter`, tabela e
  // router `/saved-filters` seguem no lugar, sem migration. Religar é
  // reescrever esta seção e o JSX; enquanto isso ninguém perde dado.
  // Os componentes de tela (SavedFiltersBar, SaveFilterDialog) foram
  // apagados — o git guarda.

  useEffect(() => {
    if (!token) { navigate('/login'); }
  }, [token, navigate]);

  // Filtros e ordem da lista, sem a paginação. São os mesmos que vão no
  // GET /contacts/ids do "Toda a pasta" — a seleção pega o que a lista
  // conta, na mesma ordem.
  const filtrosDaLista = useMemo(() => {
    const params = { sort, order: sortDir };
    if (q.trim()) params.q = q.trim();
    if (filter !== 'ALL') params.interest = filter;
    if (assignedFilter) params.assigned = assignedFilter;
    if (origemFilter) params.origem = origemFilter;
    if (qualifFilter) params.qualificacao = qualifFilter;
    if (folderFilter) params.folder = folderFilter;
    // label_ids como array — axios serializa em ?label_ids=X&label_ids=Y
    // que é o formato que FastAPI espera pra List[str] Query.
    if (activeLabelIds.size > 0) params.label_ids = Array.from(activeLabelIds);
    return params;
  }, [sort, sortDir, q, filter, assignedFilter, origemFilter, qualifFilter, folderFilter, activeLabelIds]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const res = await api.get('/contacts', {
        params: { ...filtrosDaLista, page, per_page: PER_PAGE },
        headers: { Authorization: `Bearer ${token}` },
        paramsSerializer: { indexes: null }, // repeat keys em vez de label_ids[]=...
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalAll(res.data.total_all || 0);
      setCounts(res.data.counts_by_interest || { [UNSET]: 0, ALTO: 0, MEDIO: 0, BAIXO: 0, FRIO: 0 });
      setUnassigned(res.data.unassigned || 0);
      setDeAnuncio(res.data.de_anuncio || 0);
      setContagemLead(res.data.counts_by_lead || null);
    } catch (err) {
      setListError(err.response?.data?.detail || 'Erro ao carregar contatos.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, filtrosDaLista]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Reset página + seleção quando muda escopo (filtro/busca/pasta).
  // Etiquetas entraram na lista em out/26: com o "Toda a pasta", trocar a
  // etiqueta e continuar com os ids do filtro anterior selecionados (e a
  // faixa dizendo outra coisa) seria agir sobre quem não está na tela.
  useEffect(() => {
    setPage(1);
    limparSelecao();
  }, [q, filter, assignedFilter, origemFilter, qualifFilter, folderFilter, activeLabelIds, limparSelecao]);

  // "Toda a pasta": pede ao servidor os ids de todos os contatos do filtro.
  const selecionarTudo = useCallback(async () => {
    const pedido = ++pedidoTudoRef.current;
    setSelecaoTudo({ status: 'carregando' });
    try {
      const res = await api.get('/contacts/ids', {
        params: filtrosDaLista,
        headers: { Authorization: `Bearer ${token}` },
        paramsSerializer: { indexes: null },
      });
      if (pedido !== pedidoTudoRef.current) return;
      const ids = res.data.ids || [];
      setSelectedIds(new Set(ids));
      setSelecaoTudo({ status: 'ok', n: ids.length, total: res.data.total || ids.length });
    } catch {
      if (pedido !== pedidoTudoRef.current) return;
      setSelecaoTudo({ status: 'erro' });
    }
  }, [filtrosDaLista, token]);

  // "Esta página": volta pros contatos da tela (também desiste de um
  // "Toda a pasta" que ainda está carregando).
  const selecionarPagina = useCallback(() => {
    pedidoTudoRef.current += 1;
    setSelecaoTudo(null);
    setSelectedIds(new Set(items.map(c => c.id)));
  }, [items]);

  const modoTudo = selecaoTudo?.status === 'ok' && selectedIds.size === selecaoTudo.n;
  // A faixa só faz sentido com a página inteira marcada e mais contatos no
  // filtro do que na página. Com uma linha desmarcada, a seleção é
  // escolha da pessoa e a faixa sai.
  const mostrarEscopo = items.length > 0 && total > items.length
    && (paginaToda || selecaoTudo?.status === 'carregando');
  // Como a faixa chama o "tudo": pela pasta, quando ela é o único filtro.
  const escopoSelecao = useMemo(() => {
    const pasta = folders.find(f => f.id === folderFilter);
    const outros = Boolean(q.trim()) || filter !== 'ALL' || Boolean(assignedFilter) || Boolean(origemFilter)
      || Boolean(qualifFilter) || activeLabelIds.size > 0;
    if (pasta && !outros) {
      return {
        prefixo: 'Toda a pasta', nome: pasta.name, curto: 'Pasta',
        cor: folderColor(pasta.color).dot,
        frase: `da pasta "${pasta.name}"`,
      };
    }
    if (outros) return { prefixo: 'Todos deste filtro', curto: 'Filtro', frase: 'deste filtro' };
    return { prefixo: 'Todos os contatos', curto: 'Todos', frase: 'do CRM' };
  }, [folders, folderFilter, q, filter, assignedFilter, origemFilter, qualifFilter, activeLabelIds]);

  // O contato de `?selected=` pode não estar na página atual (paginação,
  // filtro, ou veio do ChatPage). Aí ele é buscado avulso e ENTRA no
  // `items` — é só isso que este efeito faz agora.
  //
  // Antes ele também chamava `setSelectedId`, e era daí que vinha o laço
  // do "X que não fecha" (ver o comentário em `selectedId`). Hoje a
  // seleção JÁ é a URL: se o parâmetro está lá, o contato está aberto.
  // O efeito só precisa garantir que existe uma linha pra desenhar.
  //
  // `buscadosRef` evita repetir o GET: o efeito depende de `items`, e
  // cada refetch da lista (filtro, ação em lote, renomear pasta) entrega
  // um array novo — sem a trava, um id inexistente refazia a mesma
  // requisição fadada ao 404 a cada uma dessas.
  const buscadosRef = useRef(new Set());
  useEffect(() => {
    if (!selectedId || loading) return;
    if (items.some(c => c.id === selectedId)) return;
    if (buscadosRef.current.has(selectedId)) return;
    buscadosRef.current.add(selectedId);

    api.get(`/contacts/${selectedId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setItems(prev => (prev.some(c => c.id === res.data.id) ? prev : [res.data, ...prev]));
      })
      .catch(() => {
        // 404: contato de outra company, apagado, ou que só existe do
        // lado das mensagens. Limpa o parâmetro em vez de deixar a
        // pessoa numa tela sem nada aberto e sem explicação — que era o
        // "silenciosamente falhar" que o comentário antigo dizia evitar.
        setListError('Não foi possível abrir esse contato. Ele pode ter sido excluído.');
        setSelectedId(null);
      });
  }, [selectedId, items, loading, token, setSelectedId]);

  // Notas quando muda seleção
  useEffect(() => {
    if (!selectedId) { setNotes([]); return; }
    let cancelled = false;
    setNotesLoading(true);
    api.get(`/contacts/${selectedId}/notes`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => { if (!cancelled) setNotes(res.data || []); })
      .catch(() => { if (!cancelled) setNotes([]); })
      .finally(() => { if (!cancelled) setNotesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, token]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  // Clique no cabeçalho: a mesma coluna inverte a ordem; outra coluna
  // começa em asc. asc em "Situação" (silence) = quem respondeu há mais
  // tempo primeiro, que é a ordem útil pra triagem.
  const ordenarPor = (chave) => {
    if (sort === chave) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(chave); setSortDir('asc'); }
  };

  const handleCreate = async () => {
    setNewError('');
    if (!newName.trim() || !newPhone.trim()) {
      setNewError('Preencha nome e telefone.');
      return;
    }
    setNewSaving(true);
    try {
      const res = await api.post('/contacts', {
        name: newName.trim(),
        phone_number: newPhone.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewOpen(false);
      setNewName(''); setNewPhone('');
      fetchList();
      setSelectedId(res.data.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 409 estruturado: o número já existe na empresa. Em vez de só
      // recusar — que levava a pessoa a tentar outra formatação até
      // passar, criando a duplicata — abrimos o modal que mostra QUEM já
      // está cadastrado e oferece abrir ou renomear.
      if (err.response?.status === 409 && detail?.code === 'CONTATO_JA_EXISTE') {
        setNewOpen(false);
        setConflito(detail);
      } else {
        // `detail` pode ser objeto (erros de validação do FastAPI vêm como
        // lista). Renderizar objeto direto quebra o React.
        setNewError(
          typeof detail === 'string' ? detail : 'Erro ao criar contato.'
        );
      }
    } finally {
      setNewSaving(false);
    }
  };

  // ─── Conflito de número já cadastrado ─────────────────────────────────
  // `setSelectedId` escreve a URL, então o contato abre mesmo estando em
  // outra página ou fora do filtro atual — o efeito acima busca a linha
  // que faltar. (Antes isto chamava `setSearchParams` na mão pra
  // conseguir esse efeito; agora é o comportamento normal.)
  const abrirExistente = (id) => {
    setConflito(null);
    setNewName(''); setNewPhone('');
    setSelectedId(id);
  };

  const renomearExistente = async (id, nome) => {
    setConflitoSalvando(true);
    try {
      // `api.patch` direto em vez de `patchContactById`: aquele trata o
      // erro por dentro (alert + revert) e NUNCA rejeita, então um catch
      // em volta dele é código morto — uma renomeação que falhasse
      // fecharia o modal como se tivesse dado certo.
      await api.patch(`/contacts/${id}`, { name: nome }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConflito(null);
      setNewName(''); setNewPhone('');
      fetchList();
      setSelectedId(id);
    } catch (err) {
      const d = err.response?.data?.detail;
      // Modal fica aberto: a pessoa vê o motivo e decide entre tentar de
      // novo ou só abrir o contato existente.
      setConflito(prev => (prev ? {
        ...prev,
        erro: typeof d === 'string' ? d : 'Não foi possível renomear o contato.',
      } : prev));
    } finally {
      setConflitoSalvando(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setDeleteSaving(true);
    setDeleteError('');
    try {
      await api.delete(`/contacts/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteOpen(false);
      setSelectedId(null);
      fetchList();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Erro ao excluir contato.');
    } finally {
      setDeleteSaving(false);
    }
  };

  // Patch de QUALQUER contato pelo id. O `patchContact` original só
  // funcionava no `selected`, o que bastava enquanto editar era coisa de
  // modal aberto. Com a edição inline na tabela (agente e interesse), a
  // linha que muda não é necessariamente a selecionada.
  //
  // `extra` carrega os campos derivados que o backend não devolve no PATCH
  // mas a tabela mostra — hoje `assigned_user_name`. Sem isso a célula
  // ficaria com o nome antigo até o próximo fetch.
  const patchContactById = useCallback(async (id, updates, extra = {}) => {
    if (!id) return;
    const anterior = items.find(c => c.id === id);
    // Otimista: a lista responde no clique. Se o PATCH falhar, desfaz.
    setItems(prev => prev.map(c => c.id === id ? { ...c, ...updates, ...extra } : c));
    try {
      await api.patch(`/contacts/${id}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      if (anterior) setItems(prev => prev.map(c => c.id === id ? anterior : c));
      alert(err.response?.data?.detail || 'Erro ao atualizar contato.');
      fetchList();
    }
  }, [items, token, fetchList]);

  const patchContact = async (updates) => {
    if (!selected) return;
    await patchContactById(selected.id, updates);
  };

  // Campos personalizados têm handler próprio porque a forma do dado é
  // diferente nos dois sentidos: o PATCH manda um DICIONÁRIO {chave: valor}
  // (mescla parcial no backend) e o contato guarda uma LISTA ordenada de
  // {key, label, field_type, value}, que é o que o drawer desenha. O
  // `patchContactById` espalha o payload direto no objeto — aqui isso
  // trocaria a lista pelo dicionário e o drawer pararia de renderizar.
  //
  // Recebe TODOS os campos pendentes de uma vez (o drawer junta antes de
  // chamar): um PATCH em vez de um por campo.
  const saveCustomFields = useCallback(async (contact, valores) => {
    if (!contact?.id || !valores || !Object.keys(valores).length) return;
    const anterior = items.find(c => c.id === contact.id);
    setItems(prev => prev.map(c => c.id === contact.id
      ? {
          ...c,
          custom_fields: (c.custom_fields || []).map(f => (
            f.key in valores ? { ...f, value: valores[f.key] } : f
          )),
        }
      : c));
    try {
      await api.patch(`/contacts/${contact.id}`, { custom_fields: valores }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      if (anterior) setItems(prev => prev.map(c => c.id === contact.id ? anterior : c));
      alert(err.response?.data?.detail || 'Erro ao salvar os campos.');
      // Relança pra o drawer manter o rascunho: descartar o que a pessoa
      // digitou por causa de uma falha de rede é perder trabalho dela.
      throw err;
    }
  }, [items, token]);

  const handleDeleteNote = async (noteId) => {
    if (!selected) return;
    try {
      await api.delete(`/contacts/${selected.id}/notes/${noteId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir anotação.');
    }
  };

  // Wrapper que o ContactDrawer chama passando o texto direto (o composer
  // do drawer é local, não usa o `noteText` global do fluxo antigo).
  const addNoteFromDrawer = useCallback(async (text) => {
    if (!selected || !text.trim()) return;
    setNoteSaving(true);
    try {
      const res = await api.post(`/contacts/${selected.id}/notes`,
        { text: text.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setNotes(prev => [res.data, ...prev]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao salvar anotação.');
    } finally {
      setNoteSaving(false);
    }
  }, [selected, token]);

  // Attach de label / folder num contato só. Até set/26 não existia: os
  // chips "+ Etiqueta"/"+ Pasta" do drawer chamavam `setEditOpen`, que
  // abre o diálogo de EDITAR NOME E TELEFONE. Eram placeholder que nunca
  // foi religado, então anexar etiqueta a um contato individual só dava
  // pra fazer selecionando ele na lista e usando a barra de ação em lote.
  const attachLabelToSelected = useCallback(async (labelId) => {
    if (!selected) return;
    const atuais = (selected.labels || []).map(l => l.id);
    if (atuais.includes(labelId)) return;
    const nova = labels.find(l => l.id === labelId);
    try {
      await api.patch(`/contacts/${selected.id}`,
        { label_ids: [...atuais, labelId] },
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(prev => prev.map(c => c.id === selected.id
        ? { ...c, labels: [...(c.labels || []), nova].filter(Boolean) }
        : c));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao aplicar etiqueta.');
    }
  }, [selected, labels, token]);

  const attachFolderToSelected = useCallback(async (folderId) => {
    if (!selected) return;
    const nova = folders.find(f => f.id === folderId);
    try {
      // Mesmo endpoint do lote, com um id só — o backend já aceita.
      const res = await api.post(`/folders/${folderId}/clients`,
        { client_ids: [selected.id] },
        { headers: { Authorization: `Bearer ${token}` } });
      // A rota responde 201 mesmo quando não gravou nada (contato fora de
      // escopo). Confiar só no status escondeu um bug por semanas: a pasta
      // aparecia na tela e sumia ao recarregar. Agora o número manda.
      const gravou = (res.data?.added ?? 0) > 0 || (res.data?.already_in ?? 0) > 0;
      if (!gravou) {
        alert('Não foi possível adicionar à pasta. Recarregue e tente de novo.');
        return;
      }
      setItems(prev => prev.map(c => c.id === selected.id
        ? { ...c, folders: [...(c.folders || []), nova].filter(Boolean) }
        : c));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao adicionar à pasta.');
    }
  }, [selected, folders, token]);

  // Detach de label / folder — usa PATCH unitário (ainda não tem
  // endpoint dedicado; passamos label_ids sem o removido).
  const detachLabelFromSelected = useCallback(async (labelId) => {
    if (!selected) return;
    const next = (selected.labels || []).filter(l => l.id !== labelId).map(l => l.id);
    try {
      await api.patch(`/contacts/${selected.id}`,
        { label_ids: next },
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(prev => prev.map(c => c.id === selected.id
        ? { ...c, labels: (c.labels || []).filter(l => l.id !== labelId) }
        : c));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao remover etiqueta.');
    }
  }, [selected, token]);

  const detachFolderFromSelected = useCallback(async (folderId) => {
    if (!selected) return;
    try {
      await api.delete(`/folders/${folderId}/clients/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(prev => prev.map(c => c.id === selected.id
        ? { ...c, folders: (c.folders || []).filter(f => f.id !== folderId) }
        : c));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao remover da pasta.');
    }
  }, [selected, token]);

  // Timeline (nesta rodada): só o `last_inbound` como item único.
  // Audit log completo (mudanças de status, atribuições) vem em PR
  // futuro — o drawer já tem placeholder pra isso.
  const drawerTimeline = useMemo(() => {
    if (!selected?.last_inbound) return [];
    return [{
      direction: 'INBOUND',
      content: selected.last_inbound.content,
      timestamp: selected.last_inbound.timestamp,
      user_name: null,
    }];
  }, [selected]);

  const handleExport = () => {
    const url = `${process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br'}/contacts/export.csv`;
    // Passa o token via query se necessário; melhor abrir com fetch pra baixar
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `contatos_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Erro ao exportar CSV.'));
  };

  // ─── Render ────────────────────────────────────────────────────────
  // Out/26 (CRM moderno): um cartão só pra lista, com busca, ações e
  // filtros em cima — o título "Contatos" numa linha inteira saiu, a
  // TopBar já diz. Só a apresentação mudou; o estado e os handlers acima
  // são os mesmos.
  return (
    <ThemeProvider theme={muiTema}>
    <Box sx={{
      display: 'flex', minHeight: '100vh',
      bgcolor: 'var(--ch-bg)', color: 'var(--ch-text)', fontFamily: OUTFIT,
      transition: 'background-color .25s ease',
    }}>
      <AppSidebar user={user} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // `minWidth: 0` não é detalhe: item de flex nasce com
          // `min-width: auto` e por isso NÃO encolhe abaixo da largura
          // mínima do conteúdo. Sem isto o main media 843px num celular de
          // 390px e a página inteira rolava de lado.
          minWidth: 0,
          ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
          transition: 'margin-left 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <TopBar
          crumbBase="Gerenciamento"
          crumbCur={isMobile && selected ? (selected.name || 'Contato') : 'Contatos'}
          onBack={isMobile && selected ? () => setSelectedId(null) : undefined}
          themeMode={tema}
          onToggleTheme={trocarTema}
        />

        {/* A lista ocupa a altura que sobra e rola por dentro (cabeçalho
            preso). No celular o topo é alto demais pra isso: a área inteira
            rola e a lista fica com a altura mínima dela.
            Com seleção, o rodapé abre espaço pra barra de lote flutuante:
            sem isso ela cobria a paginação, e trocar de página com contatos
            marcados ficava impossível. */}
        <Box sx={{
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
          p: '20px 28px', pb: selectedIds.size > 0 ? '92px' : '20px',
          position: 'relative', isolation: 'isolate',
          overflowX: 'hidden', overflowY: 'auto',
          transition: 'padding-bottom .25s ease',
          '@media (max-width: 899.98px)': { p: '14px 12px', pb: selectedIds.size > 0 ? '92px' : '14px' },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}>
          {/* O brilho verde do topo, o mesmo da LP e do Cadastro. */}
          <Box aria-hidden sx={{
            position: 'absolute', zIndex: -1, top: -340, left: '50%', transform: 'translateX(-50%)',
            width: 1200, height: 560, pointerEvents: 'none',
            background: 'radial-gradient(closest-side, var(--cx-glow), transparent)',
          }} />

          <BarraFerramentas
            q={q}
            onBusca={setQ}
            isSupervisor={isSupervisor}
            recarregando={loading}
            onNovaPasta={() => setNewFolderOpen(true)}
            onCampos={() => setCustomFieldsOpen(true)}
            onRecarregar={fetchList}
            onImportar={() => setImportOpen(true)}
            onExportar={handleExport}
            onNovo={() => setNewOpen(true)}
          />

          <FiltrosInteresse
            filtro={filter}
            onFiltro={setFilter}
            totalAll={totalAll}
            counts={counts}
            semResp={assignedFilter === 'none'}
            onSemResp={() => setAssignedFilter(prev => (prev === 'none' ? null : 'none'))}
            unassigned={unassigned}
          />
          <BarraTemperatura filtro={filter} counts={counts} />

          <FiltrosLinha
            folders={folders}
            labels={labels}
            pastaAtiva={folderFilter}
            etiquetasAtivas={activeLabelIds}
            onAbrirPastas={(e) => setFoldersMenuAnchor(e.currentTarget)}
            onLimparPasta={() => { setFolderFilter(null); setPage(1); }}
            onAbrirEtiquetas={(e) => setLabelsMenuAnchor(e.currentTarget)}
            onLimparEtiquetas={() => { setActiveLabelIds(new Set()); setPage(1); }}
            onDisparos={() => setLembretesOpen(true)}
            deAnuncio={deAnuncio}
            soAnuncio={origemFilter === 'anuncio'}
            onSoAnuncio={() => setOrigemFilter(prev => (prev === 'anuncio' ? null : 'anuncio'))}
            qualifAtiva={qualifFilter}
            onAbrirQualif={(e) => setQualifMenuAnchor(e.currentTarget)}
            onLimparQualif={() => setQualifFilter(null)}
          />

          {/* Resultado de ação em lote / importação / disparo — some sozinho. */}
          {bulkResult && (
            <Aviso tom={bulkResult.severity || 'success'} onFechar={() => setBulkResult(null)}
              acao={bulkResult.desfazerLote ? { rotulo: 'Desfazer', onClick: () => desfazerLote(bulkResult.desfazerLote) } : null}>
              {/* `message` pronta vem do disparo de template, cujo
                  resultado não cabe na frase de "movido para pasta". */}
              {bulkResult.message
                ? bulkResult.message
                : bulkResult.added > 0
                ? `${bulkResult.added} contato${bulkResult.added !== 1 ? 's' : ''} movido${bulkResult.added !== 1 ? 's' : ''} para "${bulkResult.folderName}".`
                : `Todos os contatos selecionados já estavam em "${bulkResult.folderName}".`
              }
              {bulkResult.already_in > 0 && bulkResult.added > 0 &&
                ` (${bulkResult.already_in} já estava${bulkResult.already_in !== 1 ? 'm' : ''} na pasta)`}
            </Aviso>
          )}
          {listError && (
            <Aviso tom="error" onFechar={() => setListError('')}>{listError}</Aviso>
          )}

          {/* A faixa "Esta página / Toda a pasta" entra presa embaixo do
              cabeçalho, com as mesmas colunas. */}
          <TabelaContatos
            items={items}
            total={total}
            totalAll={totalAll}
            page={page}
            totalPages={totalPages}
            perPage={PER_PAGE}
            loading={loading}
            isSupervisor={isSupervisor}
            selectedId={selectedId}
            selectedIds={selectedIds}
            paginaToda={paginaToda}
            onToggleAll={toggleSelectAll}
            onToggle={toggleSelected}
            onOpen={setSelectedId}
            sort={sort}
            sortDir={sortDir}
            onSort={ordenarPor}
            onAgentMenu={(e, c) => setRowAgentMenu({ anchor: e.currentTarget, contact: c })}
            onInterestMenu={(e, c) => setRowInterestMenu({ anchor: e.currentTarget, contact: c })}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => Math.min(totalPages, p + 1))}
            escopo={mostrarEscopo ? (
              <EscopoSelecao
                modo={modoTudo ? 'tudo' : 'pagina'}
                naPagina={items.length}
                total={selecaoTudo?.status === 'ok' ? selecaoTudo.total : total}
                selecionados={modoTudo ? selecaoTudo.n : items.length}
                escopo={escopoSelecao}
                carregando={selecaoTudo?.status === 'carregando'}
                erro={selecaoTudo?.status === 'erro'}
                colunas={colunas(isSupervisor)}
                onPagina={selecionarPagina}
                onTudo={selecionarTudo}
              />
            ) : null}
          />

          {/* ═══ MODAL DE DETALHE ═══
              Redesign set/26. O painel de detalhes antigo (~360 linhas
              de Paper/FieldRow/Composer inline) saiu — sua substituição
              é o ContactDrawer isolado em crm/, que centraliza toda a UI
              de detalhe num componente reutilizável.

              Era drawer lateral de 488px; virou MODAL centralizado em
              out/26. O drawer roubava largura da tabela justamente quando
              ela ganhou a coluna Situação — com ele aberto, nome e agente
              ficavam espremidos a ponto de truncar. E o modal abre espaço
              pras funcionalidades que vêm depois.

              Sem wrapper de layout: `Dialog` renderiza em portal no
              document.body, então não precisa (nem deve) ocupar uma
              coluna do flex ao lado da tabela — o Box que existia aqui
              virou resíduo e saiu junto. */}
          <ContactDrawer
            open={!!selected}
            contact={selected}
            notes={notes}
            notesLoading={notesLoading}
            timeline={drawerTimeline}
            saving={noteSaving}
            isSupervisor={isSupervisor}
            onClose={() => setSelectedId(null)}
            onOpenChat={abrirConversa}
            onChangeInterest={(_, v) => patchContact({ interest_status: v })}
            onChangeMeeting={(_, v) => patchContact({ meeting_status: v })}
            onSaveCustomFields={saveCustomFields}
            onAddNote={addNoteFromDrawer}
            onDeleteNote={handleDeleteNote}
            onDetachLabel={detachLabelFromSelected}
            onDetachFolder={detachFolderFromSelected}
            allLabels={labels}
            allFolders={folders}
            onAttachLabel={attachLabelToSelected}
            onAttachFolder={attachFolderToSelected}
            agents={agents}
            onAssignAgent={(id, name) => patchContactById(selected.id, { assigned_user_id: id }, { assigned_user_name: name })}
            onEdit={(c) => setEditOpen({ name: c.name || '', phone_number: c.phone_number || '' })}
            token={token}
            onQualificado={aoQualificar}
          />
        </Box>
      </Box>

      {/* ─── BulkActionBar flutuante — CRM redesign set/26 ───────────────
          Substitui a bulk bar sticky verde antiga. Renderiza via portal
          (position: fixed) — não desloca a viewport quando aparece. */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        isSupervisor={isSupervisor}
        onAddToFolder={(e) => setBulkFolderAnchor(e?.currentTarget || document.body)}
        onAttachLabels={(e) => setBulkLabelsAnchor(e?.currentTarget || document.body)}
        onAssignAgent={(e) => setBulkAgentAnchor(e?.currentTarget || document.body)}
        onSetInterest={(e) => setBulkInterestAnchor(e?.currentTarget || document.body)}
        onQualify={(e) => setBulkQualifAnchor(e?.currentTarget || document.body)}
        onSendTemplate={() => setBulkTemplateIds(Array.from(selectedIds))}
        onDelete={() => setBulkDeleteOpen(true)}
        onClear={limparSelecao}
      />

      {/* Escolher chip pra abrir conversa com um lead que ainda não tem um.
          Só aparece quando a empresa tem mais de um — com um só, o clique
          vai direto. */}
      <Dialog open={!!escolherChip} onClose={() => setEscolherChip(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, pb: 0.5 }}>
          Conversar com {escolherChip?.name || escolherChip?.phone_number}
          <Typography sx={{ fontSize: 14, color: 'var(--cx-cap)', fontWeight: 500, mt: 0.3 }}>
            Por qual número?
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {devices.filter(d => !d.deleted_at).map(d => (
              <Box
                key={d.id}
                component="button"
                type="button"
                onClick={() => {
                  navigate(`/chat/${d.id}/${escolherChip.id}`);
                  setEscolherChip(null);
                }}
                sx={{
                  textAlign: 'left', fontFamily: OUTFIT, color: 'var(--ch-text)',
                  bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-bdr)', borderRadius: '12px', p: 1.5,
                  cursor: 'pointer', transition: 'border-color .15s, background-color .15s',
                  '&:hover': { borderColor: 'var(--ch-green)', bgcolor: 'var(--ch-green-bg)' },
                  '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
                }}
              >
                <Box sx={{ fontSize: 15, fontWeight: 600 }}>{d.name}</Box>
                <Box sx={{ fontSize: 13.5, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                  {d.phone_number || 'sem número'}
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEscolherChip(null)} sx={{ color: 'var(--ch-text2)' }}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disparos agendados: agenda template pra sair depois. `selectedIds`
          vai junto pra oferecer "os N contatos selecionados" como alvo sem
          a pessoa ter que remarcar tudo dentro do diálogo. */}
      <LembretesDialog
        open={lembretesOpen}
        onClose={() => setLembretesOpen(false)}
        token={token}
        folders={folders}
        selectedIds={Array.from(selectedIds)}
      />

      <ContatoExisteDialog
        conflito={conflito}
        salvando={conflitoSalvando}
        onClose={() => setConflito(null)}
        onAbrir={abrirExistente}
        onRenomear={renomearExistente}
      />

      {/* Importação por planilha. A conferência do que vai entrar
          acontece lá dentro, antes de gravar qualquer contato. */}
      <ImportCsvDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={({ criados, atualizados, total_erros: erros }) => {
          fetchList();
          fetchFolders();
          setBulkResult({
            message: `${criados} contato(s) criado(s), ${atualizados} atualizado(s)`
              + (erros ? `, ${erros} com problema.` : '.'),
            severity: erros ? 'warning' : 'success',
          });
          fecharAvisoEm(6000);
        }}
      />

      {/* Disparo de template para os contatos selecionados. A conferência
          ("N prontos · M sem o dado") acontece lá dentro, antes de sair
          qualquer mensagem. */}
      <BulkTemplateDialog
        open={Boolean(bulkTemplateIds)}
        clientIds={bulkTemplateIds || []}
        onClose={() => setBulkTemplateIds(null)}
        onDone={({ enviados, falhas }) => {
          limparSelecao();
          setBulkResult({
            message: falhas
              ? `${enviados} template(s) enviado(s), ${falhas} não saiu(íram).`
              : `${enviados} template(s) enviado(s).`,
            severity: falhas ? 'warning' : 'success',
          });
          fecharAvisoEm(5000);
        }}
      />

      {/* Dialog bulk delete — confirmação obrigatória por segurança */}
      <Dialog open={bulkDeleteOpen} onClose={() => !bulkSaving && setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800 }}>
          Excluir {selectedIds.size} contato{selectedIds.size === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          {/* Com "Toda a pasta", a maior parte do que sai não está na
              tela — o diálogo diz isso antes do clique, não depois. */}
          {modoTudo && (
            <Typography variant="body2" sx={{ color: 'var(--ch-text)', fontWeight: 600, mb: 1 }}>
              São todos os contatos {escopoSelecao.frase}, inclusive os que
              estão nas outras páginas.
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: 'var(--ch-text2)' }}>
            Os contatos saem do CRM, mas as conversas continuam no chat.
            Essa ação é reversível pelo suporte.
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: 'var(--cx-cap)', mt: 1 }}>
            Se a ideia é corrigir contatos "Novo …", edite o nome em vez de excluir.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkSaving} sx={{ color: 'var(--ch-text2)' }}>
            Cancelar
          </Button>
          <Button onClick={() => doBulkAction('delete')} disabled={bulkSaving} variant="contained" color="error" disableElevation>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: campos personalizados. Ao criar/remover/renomear um campo,
          recarrega a lista — os contatos carregam a definição junto do
          valor, então sem o refetch a coluna nova só apareceria no próximo
          carregamento da página. */}
      <CustomFieldsDialog
        open={customFieldsOpen}
        onClose={() => setCustomFieldsOpen(false)}
        onChanged={fetchList}
      />

      {/* Dialog: Novo Contato */}
      <Dialog open={newOpen} onClose={() => !newSaving && setNewOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800 }}>Novo contato</DialogTitle>
        <DialogContent>
          {newError && <Alert severity="error" sx={{ mb: 2 }}>{newError}</Alert>}
          <Stack spacing={2} mt={1}>
            <TextField
              label="Nome"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              fullWidth size="small"
              autoFocus
            />
            <TextField
              label="Telefone (com DDD)"
              value={newPhone}
              onChange={e => setNewPhone(e.target.value)}
              fullWidth size="small"
              placeholder="Ex: 5521999998888"
              helperText="Sem +, apenas dígitos. Ex: 5521999998888"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNewOpen(false)} disabled={newSaving} sx={{ color: 'var(--ch-text2)' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={newSaving}
            variant="contained"
            disableElevation
            startIcon={newSaving ? <CircularProgress size={12} color="inherit" /> : <FiPlus size={14} />}
          >
            {newSaving ? 'Criando…' : 'Criar contato'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Excluir contato */}
      <Dialog open={deleteOpen} onClose={() => !deleteSaving && setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '10px',
            bgcolor: 'var(--ch-red-bg)', color: 'var(--ch-red-d)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FiAlertTriangle size={17} />
          </Box>
          Excluir contato
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--ch-text2)', mb: 1 }}>
            Tem certeza que deseja excluir o contato <b>{selected?.name}</b>?
          </Typography>
          {/* Out/26, Pontal: 86 contatos "Novo …" foram excluídos em massa pra
              "limpar" a base, e as conversas continuaram no chat presas a
              eles. Excluir não tira a conversa do chat — quem quer corrigir
              o nome precisa editar, e o aviso diz isso antes do clique. */}
          <Typography variant="caption" component="div" sx={{ color: 'var(--cx-cap)' }}>
            O contato sai do CRM, mas a conversa continua no chat. Se a ideia é
            corrigir o nome, feche e edite o contato em vez de excluir.
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteSaving} sx={{ color: 'var(--ch-text2)' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleteSaving}
            variant="contained"
            color="error"
            disableElevation
            startIcon={deleteSaving ? <CircularProgress size={12} color="inherit" /> : <FiTrash2 size={14} />}
          >
            {deleteSaving ? 'Excluindo…' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Modal: Editar contato ═══
          Escopo mínimo — apenas nome e telefone (os campos que o usuário
          costuma reajustar após criar o contato). Interesse, agente,
          reunião, etiquetas seguem editáveis inline no painel de detalhes.
          Telefone é validado no backend (formato + unicidade). */}
      <Dialog
        open={Boolean(editOpen)}
        onClose={() => !editSaving && setEditOpen(null)}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '10px',
            bgcolor: 'var(--ch-inset)', color: 'var(--ch-text2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FiEdit2 size={16} />
          </Box>
          Editar contato
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Nome"
              value={editOpen?.name || ''}
              onChange={(e) => setEditOpen(prev => ({ ...prev, name: e.target.value }))}
              fullWidth size="small" autoFocus
              inputProps={{ maxLength: 100 }}
              disabled={editSaving}
            />
            <TextField
              label="Telefone (com DDI)"
              value={editOpen?.phone_number || ''}
              onChange={(e) => setEditOpen(prev => ({ ...prev, phone_number: e.target.value }))}
              fullWidth size="small"
              placeholder="Ex: 5521999998888"
              helperText="Só dígitos, com DDI (55 pro Brasil)."
              disabled={editSaving}
            />
          </Stack>
          {editError && <Alert severity="error" sx={{ mt: 2 }}>{editError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(null)} disabled={editSaving} sx={{ color: 'var(--ch-text2)' }}>
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              if (!selected || !editOpen) return;
              const name = (editOpen.name || '').trim();
              const phone = (editOpen.phone_number || '').replace(/\D/g, '');
              if (!name) { setEditError('Nome é obrigatório.'); return; }
              if (phone.length < 8) { setEditError('Telefone precisa ter pelo menos 8 dígitos.'); return; }
              setEditSaving(true); setEditError('');
              try {
                const patch = {};
                if (name !== selected.name) patch.name = name;
                if (phone !== selected.phone_number) patch.phone_number = phone;
                if (Object.keys(patch).length === 0) {
                  setEditOpen(null); return;
                }
                const res = await api.patch(`/contacts/${selected.id}`, patch, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                // Atualiza local — reflexo imediato em list + detail.
                setSelectedId(res.data.id);
                setItems(list => list.map(c => c.id === selected.id ? { ...c, ...res.data } : c));
                setEditOpen(null);
              } catch (err) {
                const d = err.response?.data?.detail;
                setEditError(typeof d === 'string' ? d : 'Erro ao salvar contato.');
              } finally { setEditSaving(false); }
            }}
            disabled={editSaving}
            variant="contained"
            disableElevation
            startIcon={editSaving ? <CircularProgress size={12} color="inherit" /> : <FiCheck size={14} />}
          >
            {editSaving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Modal: Criar pasta ═══
          Compacto: campo nome + as 6 cores. A cor é a do ícone da pasta
          nos filtros, nos menus e no contato. */}
      <Dialog
        open={newFolderOpen}
        onClose={() => !folderSaving && setNewFolderOpen(false)}
        fullWidth maxWidth="xs"
      >
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ display: 'flex', color: 'var(--ch-green)' }}><FiFolderPlus size={18} /></Box>
          Nova pasta
        </DialogTitle>
        <DialogContent>
          {folderError && <Alert severity="error" sx={{ mb: 2 }}>{folderError}</Alert>}
          <Stack spacing={2.5} mt={1}>
            <TextField
              label="Nome da pasta"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              fullWidth size="small"
              autoFocus
              placeholder="Ex: Recreio, Campanha Julho, Leads Frios"
              onKeyDown={e => { if (e.key === 'Enter' && !folderSaving) createFolder(); }}
            />
            <Box>
              <Typography id="cor-da-pasta" sx={{
                fontFamily: OUTFIT, fontSize: 12, fontWeight: 600,
                color: 'var(--cx-cap)', letterSpacing: '0.14em',
                textTransform: 'uppercase', mb: 1,
              }}>
                Cor
              </Typography>
              <Stack direction="row" spacing={1} role="radiogroup" aria-labelledby="cor-da-pasta">
                {FOLDER_COLOR_KEYS.map(key => {
                  const tom = folderColor(key);
                  const active = newFolderColor === key;
                  return (
                    <Box
                      key={key}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={active}
                      aria-label={NOME_COR[key]}
                      title={NOME_COR[key]}
                      onClick={() => setNewFolderColor(key)}
                      sx={{
                        width: 36, height: 36, p: 0, borderRadius: '10px',
                        bgcolor: tom.bg, color: tom.color,
                        border: `2px solid ${active ? tom.dot : 'transparent'}`,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform .15s, border-color .15s',
                        '&:hover': { transform: 'scale(1.08)' },
                        '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
                        '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
                      }}
                    >
                      <FiFolder size={15} />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNewFolderOpen(false)} disabled={folderSaving} sx={{ color: 'var(--ch-text2)' }}>
            Cancelar
          </Button>
          <Button
            onClick={createFolder}
            disabled={folderSaving || !newFolderName.trim()}
            variant="contained"
            disableElevation
            startIcon={folderSaving ? <CircularProgress size={12} color="inherit" /> : <FiPlus size={14} />}
          >
            {folderSaving ? 'Criando…' : 'Criar pasta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Menus ───────────────────────────────────────────────────────
          Todos com o mesmo desenho (crm/MenuCrm): título em caixa-alta,
          bolinha de cor (interesse, etiqueta) ou pasta colorida e o ✓ no
          que está marcado. Os da barra de lote abrem pra CIMA. */}

      {/* Menus de edição inline da tabela. Deixam o supervisor atribuir
          agente e mudar interesse sem abrir o contato — que era o único
          caminho até out/26. Numa triagem de 479 contatos, abrir e fechar
          o modal por linha é o gargalo. */}
      <MenuCrm anchorEl={rowAgentMenu?.anchor || null} onClose={() => setRowAgentMenu(null)} titulo="Atribuir a" largura={240}>
        <ItemMenu
          icone={<FiUserX size={15} />}
          apagado
          marcado={!rowAgentMenu?.contact?.assigned_user_id}
          onClick={() => {
            patchContactById(rowAgentMenu.contact.id, { assigned_user_id: null }, { assigned_user_name: null });
            setRowAgentMenu(null);
          }}
        >
          Sem responsável
        </ItemMenu>
        {agents.length === 0 && <NotaMenu>Nenhum agente cadastrado.</NotaMenu>}
        {agents.map(a => (
          <ItemMenu
            key={a.id}
            icone={<FiUser size={15} />}
            marcado={rowAgentMenu?.contact?.assigned_user_id === a.id}
            onClick={() => {
              patchContactById(rowAgentMenu.contact.id, { assigned_user_id: a.id }, { assigned_user_name: a.name });
              setRowAgentMenu(null);
            }}
          >
            {a.name}
          </ItemMenu>
        ))}
      </MenuCrm>

      <MenuCrm anchorEl={rowInterestMenu?.anchor || null} onClose={() => setRowInterestMenu(null)} titulo="Nível de interesse" largura={210}>
        {INTEREST_ORDER.map(lvl => (
          <ItemMenu
            key={lvl}
            ponto={CRM_THEME.interest[lvl].dot}
            marcado={rowInterestMenu?.contact?.interest_status === lvl}
            onClick={() => {
              patchContactById(rowInterestMenu.contact.id, { interest_status: lvl });
              setRowInterestMenu(null);
            }}
          >
            {INTEREST_LABELS[lvl]}
          </ItemMenu>
        ))}
        <DivisorMenu />
        {/* Desclassificar: volta o contato pra fila de triagem. Marcado é a
            AUSÊNCIA de valor — por isso a negação em vez de comparar com a
            string sentinela, que só existe no transporte. */}
        <ItemMenu
          tracejado
          apagado
          marcado={!rowInterestMenu?.contact?.interest_status}
          onClick={() => {
            patchContactById(rowInterestMenu.contact.id, { interest_status: UNSET });
            setRowInterestMenu(null);
          }}
        >
          {INTEREST_LABELS[UNSET]}
        </ItemMenu>
      </MenuCrm>

      {/* Filtro: PASTAS (uma só). O ⋯ de cada pasta (renomear/apagar)
          aparece no hover — e sempre, em tela de toque. */}
      <MenuCrm anchorEl={foldersMenuAnchor} onClose={() => setFoldersMenuAnchor(null)} titulo="Filtrar por pasta" largura={250}>
        <ItemMenu
          icone={<Box component="span" sx={{ width: 13, height: 13, border: '1.5px dashed var(--ch-bdr2)', borderRadius: '3px' }} />}
          marcado={folderFilter === null}
          onClick={() => { setFolderFilter(null); setPage(1); setFoldersMenuAnchor(null); }}
        >
          Todas as pastas
        </ItemMenu>
        {folders.length > 0 && <DivisorMenu />}
        {folders.map(f => (
          <ItemMenu
            key={f.id}
            pasta={folderColor(f.color).color}
            marcado={folderFilter === f.id}
            onClick={() => { setFolderFilter(f.id); setPage(1); setFoldersMenuAnchor(null); }}
            fim={(
              <Box
                component="button"
                type="button"
                aria-label={`Opções da pasta ${f.name}`}
                title="Renomear ou apagar"
                onClick={(e) => {
                  e.stopPropagation();
                  setFoldersMenuAnchor(null);
                  setFolderMenu({ anchor: e.currentTarget, folder: f });
                }}
                sx={{
                  width: 26, height: 26, p: 0, border: 0, borderRadius: '8px', bgcolor: 'transparent',
                  color: 'var(--cx-cap)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  '&:hover': { bgcolor: 'var(--ch-bdr)', color: 'var(--ch-text)' },
                }}
              >
                <FiMoreHorizontal size={15} />
              </Box>
            )}
          >
            {f.name}
          </ItemMenu>
        ))}
      </MenuCrm>

      {/* Filtro: ETIQUETAS (várias). O clique marca/desmarca e o menu NÃO
          fecha — dá pra escolher várias de uma vez. */}
      <MenuCrm
        anchorEl={labelsMenuAnchor}
        onClose={() => setLabelsMenuAnchor(null)}
        titulo="Filtrar por etiqueta"
        largura={260}
        acaoTitulo={activeLabelIds.size > 0 ? (
          <Box
            component="button"
            type="button"
            onClick={() => { setActiveLabelIds(new Set()); setPage(1); }}
            sx={{
              border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer',
              fontFamily: OUTFIT, fontSize: 12.5, fontWeight: 600, letterSpacing: 0, textTransform: 'none',
              color: 'var(--ch-red-d)', '&:hover': { textDecoration: 'underline' },
            }}
          >
            Limpar ({activeLabelIds.size})
          </Box>
        ) : null}
      >
        {labels.length === 0 && (
          <NotaMenu>Nenhuma etiqueta criada. Crie no chat, ao lado do nome do contato.</NotaMenu>
        )}
        {labels.map(l => (
          <ItemMenu
            key={l.id}
            ponto={labelColor(l.color).dot}
            marcado={activeLabelIds.has(l.id)}
            onClick={() => {
              const next = new Set(activeLabelIds);
              if (next.has(l.id)) next.delete(l.id);
              else next.add(l.id);
              setActiveLabelIds(next);
              setPage(1);
            }}
          >
            {l.name}
          </ItemMenu>
        ))}
        {labels.length > 0 && <DivisorMenu />}
        {labels.length > 0 && (
          <NotaMenu>Contatos com <b>qualquer</b> etiqueta selecionada aparecem.</NotaMenu>
        )}
      </MenuCrm>

      {/* Menu por pasta (renomear / apagar), aberto pelo ⋯ do filtro. */}
      <MenuCrm anchorEl={folderMenu.anchor} onClose={() => setFolderMenu({ anchor: null, folder: null })} largura={190}>
        <ItemMenu icone={<FiEdit2 size={14} />} onClick={() => folderMenu.folder && renameFolder(folderMenu.folder)}>
          Renomear
        </ItemMenu>
        <ItemMenu perigo icone={<FiTrash2 size={14} />} onClick={() => folderMenu.folder && deleteFolder(folderMenu.folder)}>
          Apagar pasta
        </ItemMenu>
      </MenuCrm>

      {/* ─── Menus da barra de lote (abrem pra cima) ─────────────────── */}
      <MenuCrm anchorEl={bulkFolderAnchor} onClose={() => setBulkFolderAnchor(null)} titulo="Adicionar a pasta" acima largura={240}>
        {folders.length === 0 && (
          <NotaMenu>Nenhuma pasta criada. Crie em “Pasta”, ao lado da busca.</NotaMenu>
        )}
        {folders.map(f => (
          <ItemMenu key={f.id} pasta={folderColor(f.color).color} onClick={() => bulkAddToFolder(f)}>
            {f.name}
          </ItemMenu>
        ))}
      </MenuCrm>

      <MenuCrm anchorEl={bulkLabelsAnchor} onClose={() => setBulkLabelsAnchor(null)} titulo="Aplicar etiqueta" acima>
        {labels.length === 0 && (
          <NotaMenu>Crie uma etiqueta primeiro — no chat, ao lado do nome do contato.</NotaMenu>
        )}
        {labels.map(lb => (
          <ItemMenu
            key={lb.id}
            ponto={labelColor(lb.color).dot}
            disabled={bulkSaving}
            onClick={() => doBulkAction('attach_labels', { label_ids: [lb.id] })}
          >
            {lb.name}
          </ItemMenu>
        ))}
      </MenuCrm>

      {/* Atribuir agente em lote (só supervisor — a barra nem mostra o
          botão pro agente, e o backend recusa). */}
      <MenuCrm anchorEl={bulkAgentAnchor} onClose={() => setBulkAgentAnchor(null)} titulo="Atribuir agente" acima largura={240}>
        <ItemMenu
          icone={<FiUserX size={15} />}
          apagado
          disabled={bulkSaving}
          onClick={() => doBulkAction('assign_agent', { assigned_user_id: null })}
        >
          Sem responsável
        </ItemMenu>
        {agents.length === 0 && <NotaMenu>Nenhum agente cadastrado.</NotaMenu>}
        {agents.map(({ id, name }) => (
          <ItemMenu
            key={id}
            icone={<FiUser size={15} />}
            disabled={bulkSaving}
            onClick={() => doBulkAction('assign_agent', { assigned_user_id: id })}
          >
            {name || id}
          </ItemMenu>
        ))}
      </MenuCrm>

      <MenuCrm anchorEl={bulkInterestAnchor} onClose={() => setBulkInterestAnchor(null)} titulo="Interesse" acima largura={210}>
        {INTEREST_ORDER.map(k => (
          <ItemMenu
            key={k}
            ponto={CRM_THEME.interest[k].dot}
            disabled={bulkSaving}
            onClick={() => doBulkAction('set_interest', { interest_status: k })}
          >
            {INTEREST_LABELS[k]}
          </ItemMenu>
        ))}
        <DivisorMenu />
        {/* Desclassificar em lote — devolve os contatos pra fila de
            triagem. Separado por divisor: é remover a classificação, não
            escolher mais um nível. */}
        <ItemMenu
          tracejado
          apagado
          disabled={bulkSaving}
          onClick={() => doBulkAction('set_interest', { interest_status: UNSET })}
        >
          {INTEREST_LABELS[UNSET]}
        </ItemMenu>
      </MenuCrm>

      {/* Qualificação (rastreamento PR 2): o filtro da lista e o lote. */}
      <MenuCrm anchorEl={qualifMenuAnchor} onClose={() => setQualifMenuAnchor(null)} titulo="Filtrar por qualificação" largura={250}>
        {['qualificado', 'desqualificado', 'venda', 'sem'].map(k => (
          <ItemMenu
            key={k}
            ponto={QUALIF[k]?.ponto}
            tracejado={k === 'sem'}
            apagado={k === 'sem'}
            marcado={qualifFilter === k}
            onClick={() => { setQualifFilter(prev => (prev === k ? null : k)); setQualifMenuAnchor(null); }}
          >
            <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
              <span>{ROTULO_FILTRO_QUALIF[k]}</span>
              <Box component="span" sx={{ color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                {(contagemLead?.[k] ?? 0).toLocaleString('pt-BR')}
              </Box>
            </Box>
          </ItemMenu>
        ))}
      </MenuCrm>
      <PainelQualificar
        anchorEl={bulkQualifAnchor}
        onClose={() => setBulkQualifAnchor(null)}
        lote
        acima
        titulo={`Marcar ${selectedIds.size.toLocaleString('pt-BR')} contato${selectedIds.size !== 1 ? 's' : ''}`}
        onEscolher={qualificarLote}
      />
    </Box>
    </ThemeProvider>
  );
}

// ─── Componentes auxiliares ─────────────────────────────────────────────
